import {
  WhiteboardElement,
  WhiteboardPage,
  WhiteboardTool,
  renderElementsToCanvas,
  generatePageSVG,
  generateMultiPagePDF,
  renderPageHighRes,
  downloadBlob,
} from '../services/whiteboardExport';

export type SubjectMode = 'general' | 'database' | 'electronics' | 'flowchart' | 'math';

export interface DrawAction {
  type: 'move' | 'shape' | 'clear' | 'element_add' | 'page_add' | 'page_switch' | 'page_delete';
  tool?: string;
  color?: string;
  size?: number;
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
  pageIndex?: number;
  element?: WhiteboardElement;
}

export class WhiteboardModal {
  private containerEl: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private isVisible = false;
  private roomId = '';

  // Multi-page System
  private pages: WhiteboardPage[] = [
    { id: 'page-1', pageNumber: 1, title: 'Page 1', elements: [] }
  ];
  private currentPageIndex = 0;

  // Subject Modes
  private subjectMode: SubjectMode = 'general';

  // Tool states
  private currentTool: WhiteboardTool = 'pen';
  private currentColor = '#60a5fa';
  private currentSize = 4;
  private startX = 0;
  private startY = 0;
  private currentStrokePoints: { x: number; y: number }[] = [];

  // Zoom / Viewport
  private zoomScale = 1.0;

  // Per-page Undo/Redo history of elements
  private undoStacks: Map<string, WhiteboardElement[][]> = new Map();
  private redoStacks: Map<string, WhiteboardElement[][]> = new Map();

  public onDrawAction?: (action: DrawAction) => void;
  public onClose?: () => void;

  constructor(roomId: string) {
    this.roomId = roomId;
    this.initPageHistory('page-1');
  }

  private initPageHistory(pageId: string) {
    if (!this.undoStacks.has(pageId)) this.undoStacks.set(pageId, []);
    if (!this.redoStacks.has(pageId)) this.redoStacks.set(pageId, []);
  }

  public getIsOpen(): boolean {
    return this.isVisible;
  }

  public hasDrawings(): boolean {
    return this.pages.some(p => p.elements.length > 0);
  }

  public toggle(container: HTMLElement) {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.render(container);
    } else {
      this.hide();
    }
  }

  public hide() {
    this.isVisible = false;
    if (this.containerEl) {
      this.containerEl.innerHTML = '';
    }
    if (this.onClose) this.onClose();
  }

  public render(container: HTMLElement) {
    this.containerEl = container;
    const curPage = this.pages[this.currentPageIndex];

    container.innerHTML = `
      <div class="modal-backdrop" style="z-index: 1000; padding: 1rem;">
        <div class="modal-card" style="width: 96vw; max-width: 1400px; height: 92vh; max-height: 920px; display: flex; flex-direction: column; padding: 0; overflow: hidden; background: #18191e; border: 1px solid var(--border-color); box-shadow: var(--shadow-2xl); border-radius: var(--radius-xl);">
          
          <!-- Top Row 1: Subject Mode Tabs & Actions -->
          <div style="padding: 8px 16px; background: rgba(24, 25, 30, 0.98); border-bottom: 1px solid var(--border-subtle); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; z-index: 10;">
            <!-- Left: Title & Subject Selector -->
            <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
              <span style="font-weight: 800; font-size: 1rem; color: white; display: flex; align-items: center; gap: 6px;">
                <span>🎨</span> Verve Studio
              </span>

              <div class="wb-subject-bar" id="wb-subject-tabs">
                <button type="button" class="wb-subject-tab ${this.subjectMode === 'general' ? 'active' : ''}" data-subject="general">
                  ✏️ Sketching
                </button>
                <button type="button" class="wb-subject-tab ${this.subjectMode === 'database' ? 'active' : ''}" data-subject="database">
                  🗄️ Database & ERD
                </button>
                <button type="button" class="wb-subject-tab ${this.subjectMode === 'electronics' ? 'active' : ''}" data-subject="electronics">
                  ⚡ Electronics Lab
                </button>
                <button type="button" class="wb-subject-tab ${this.subjectMode === 'flowchart' ? 'active' : ''}" data-subject="flowchart">
                  📊 Flowchart
                </button>
                <button type="button" class="wb-subject-tab ${this.subjectMode === 'math' ? 'active' : ''}" data-subject="math">
                  📐 Math & Physics
                </button>
              </div>
            </div>

            <!-- Right: Zoom, Download & Close -->
            <div style="display: flex; align-items: center; gap: 8px;">
              <div class="wb-zoom-controls">
                <button type="button" id="btn-zoom-out" class="wb-zoom-btn" title="Zoom Out">−</button>
                <span id="lbl-zoom-level" style="font-size: 0.75rem; color: var(--text-secondary); min-width: 38px; text-align: center;">${Math.round(this.zoomScale * 100)}%</span>
                <button type="button" id="btn-zoom-in" class="wb-zoom-btn" title="Zoom In">+</button>
                <button type="button" id="btn-zoom-reset" class="wb-zoom-btn" title="Reset Zoom">⟲</button>
              </div>

              <button type="button" id="btn-wb-undo" class="control-btn" style="width: 32px; height: 32px;" title="Undo (Cmd+Z)">
                ↩️
              </button>
              <button type="button" id="btn-wb-redo" class="control-btn" style="width: 32px; height: 32px;" title="Redo (Cmd+Y)">
                ↪️
              </button>
              <button type="button" id="btn-wb-clear" class="control-btn" style="width: 32px; height: 32px;" title="Clear Current Page">
                🗑️
              </button>

              <button type="button" id="btn-download-artifact" class="btn-primary" style="padding: 6px 14px; font-size: 0.825rem; display: flex; align-items: center; gap: 6px; background: linear-gradient(135deg, #0284c7, #6366f1);" title="Download Vector SVG / Multi-page PDF / 8K Image">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span>Download Artifact</span>
              </button>

              <button type="button" id="btn-close-wb" class="control-btn" style="width: 32px; height: 32px;" title="Close Whiteboard">
                ✕
              </button>
            </div>
          </div>

          <!-- Top Row 2: Dynamic Subject Tool Palette & Color/Size -->
          <div style="padding: 6px 16px; background: rgba(20, 21, 26, 0.95); border-bottom: 1px solid var(--border-subtle); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; z-index: 9;">
            <!-- Active Subject Tools -->
            <div id="wb-dynamic-tools" style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
              ${this.renderSubjectToolsHTML()}
            </div>

            <!-- Color Palette & Stroke Size -->
            <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
              <div style="display: flex; align-items: center; gap: 6px; background: rgba(255, 255, 255, 0.05); padding: 3px 8px; border-radius: var(--radius-md);">
                ${['#60a5fa', '#34d399', '#f87171', '#fbbf24', '#c084fc', '#f472b6', '#38bdf8', '#ffffff', '#374151']
                  .map(c => `
                    <button type="button" class="color-dot" data-color="${c}" style="width: 18px; height: 18px; border-radius: 50%; background: ${c}; border: 2px solid ${this.currentColor === c ? 'white' : 'transparent'}; cursor: pointer; padding: 0;"></button>
                  `).join('')}
              </div>

              <div style="display: flex; align-items: center; gap: 6px; font-size: 0.75rem; color: var(--text-muted);">
                <span>Size</span>
                <input type="range" id="input-stroke-size" min="1" max="32" value="${this.currentSize}" style="width: 65px; accent-color: var(--brand-primary);" />
              </div>
            </div>
          </div>

          <!-- Canvas Stage with Multi-Page Floating Controls -->
          <div id="whiteboard-canvas-container" style="flex: 1; position: relative; width: 100%; height: 100%; overflow: hidden; cursor: crosshair; background: #18191e;">
            <canvas id="whiteboard-canvas" style="position: absolute; inset: 0;"></canvas>

            <!-- Bottom Floating Pagination Bar -->
            <div style="position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); z-index: 20;">
              <div class="wb-pagination-bar">
                <button type="button" id="btn-page-prev" class="wb-page-btn" ${this.currentPageIndex === 0 ? 'disabled' : ''}>
                  ◀ Prev
                </button>
                <div class="wb-page-counter" id="lbl-page-counter">
                  Page ${curPage.pageNumber} of ${this.pages.length}
                </div>
                <button type="button" id="btn-page-next" class="wb-page-btn" ${this.currentPageIndex === this.pages.length - 1 ? 'disabled' : ''}>
                  Next ▶
                </button>
                <div style="width: 1px; height: 16px; background: rgba(255, 255, 255, 0.15); margin: 0 4px;"></div>
                <button type="button" id="btn-page-add" class="wb-page-btn" title="Add Infinity Page" style="color: #38bdf8; font-weight: 700;">
                  ＋ Add Page
                </button>
                <button type="button" id="btn-page-delete" class="wb-page-btn" title="Delete Current Page" ${this.pages.length <= 1 ? 'disabled' : ''} style="color: #f87171;">
                  🗑️ Delete
                </button>
              </div>
            </div>
          </div>

          <!-- Export Artifact Modal Mount -->
          <div id="wb-export-modal-mount"></div>
        </div>
      </div>
    `;

    this.initCanvas(container);
    this.bindEvents(container);
  }

  private renderSubjectToolsHTML(): string {
    switch (this.subjectMode) {
      case 'database':
        return `
          <button type="button" class="btn-tool ${this.currentTool === 'db_table' ? 'active' : ''}" data-tool="db_table" title="Database Entity Table (PK, FK, Columns)">
            🗄️ Table Entity
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'db_relation' ? 'active' : ''}" data-tool="db_relation" title="1-to-Many Crow's Foot Connector">
            🔗 1..* Relation
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'db_attr' ? 'active' : ''}" data-tool="db_attr" title="Schema Attribute Pill">
            🏷️ Attribute
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'pen' ? 'active' : ''}" data-tool="pen" title="Freehand Pen">
            ✏️ Pen
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'eraser' ? 'active' : ''}" data-tool="eraser" title="Eraser">
            🧽 Eraser
          </button>
        `;

      case 'electronics':
        return `
          <button type="button" class="btn-tool ${this.currentTool === 'el_resistor' ? 'active' : ''}" data-tool="el_resistor" title="Resistor (Zig-zag symbol)">
            ⚡ Resistor
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'el_capacitor' ? 'active' : ''}" data-tool="el_capacitor" title="Capacitor (Parallel plates)">
            🔋 Capacitor
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'el_voltage' ? 'active' : ''}" data-tool="el_voltage" title="DC Power Source / Battery">
            ⚡ Vcc Battery
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'el_ground' ? 'active' : ''}" data-tool="el_ground" title="Ground GND">
            ⏚ Ground
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'el_and' ? 'active' : ''}" data-tool="el_and" title="Logic AND Gate">
            AND Gate
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'el_or' ? 'active' : ''}" data-tool="el_or" title="Logic OR Gate">
            OR Gate
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'el_not' ? 'active' : ''}" data-tool="el_not" title="Logic NOT Inverter Gate">
            NOT Gate
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'line' ? 'active' : ''}" data-tool="line" title="Conductor Wire">
            ➖ Wire
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'eraser' ? 'active' : ''}" data-tool="eraser" title="Eraser">
            🧽 Eraser
          </button>
        `;

      case 'flowchart':
        return `
          <button type="button" class="btn-tool ${this.currentTool === 'fc_process' ? 'active' : ''}" data-tool="fc_process" title="Process Step Block">
            🔲 Process
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'fc_decision' ? 'active' : ''}" data-tool="fc_decision" title="Decision Diamond">
            💎 Decision
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'fc_terminal' ? 'active' : ''}" data-tool="fc_terminal" title="Start/End Terminal">
            ⭕ Start/End
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'arrow' ? 'active' : ''}" data-tool="arrow" title="Flow Arrow">
            ➡️ Arrow
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'pen' ? 'active' : ''}" data-tool="pen" title="Freehand Pen">
            ✏️ Pen
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'eraser' ? 'active' : ''}" data-tool="eraser" title="Eraser">
            🧽 Eraser
          </button>
        `;

      case 'math':
        return `
          <button type="button" class="btn-tool ${this.currentTool === 'math_grid' ? 'active' : ''}" data-tool="math_grid" title="Cartesian Coordinate Grid">
            ▦ Grid
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'math_axes' ? 'active' : ''}" data-tool="math_axes" title="Coordinate X-Y Axes">
            📈 X-Y Axes
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'line' ? 'active' : ''}" data-tool="line" title="Vector / Line">
            ➖ Line
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'circle' ? 'active' : ''}" data-tool="circle" title="Circle">
            ⭕ Circle
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'pen' ? 'active' : ''}" data-tool="pen" title="Freehand Pen">
            ✏️ Pen
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'eraser' ? 'active' : ''}" data-tool="eraser" title="Eraser">
            🧽 Eraser
          </button>
        `;

      case 'general':
      default:
        return `
          <button type="button" class="btn-tool ${this.currentTool === 'pen' ? 'active' : ''}" data-tool="pen" title="Pen">
            ✏️ Pen
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'highlighter' ? 'active' : ''}" data-tool="highlighter" title="Marker / Highlighter">
            🖍️ Marker
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'eraser' ? 'active' : ''}" data-tool="eraser" title="Eraser">
            🧽 Eraser
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'line' ? 'active' : ''}" data-tool="line" title="Line">
            ➖ Line
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'arrow' ? 'active' : ''}" data-tool="arrow" title="Arrow">
            ➡️ Arrow
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'rect' ? 'active' : ''}" data-tool="rect" title="Rectangle Box">
            🔲 Box
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'circle' ? 'active' : ''}" data-tool="circle" title="Circle">
            ⭕ Circle
          </button>
          <button type="button" class="btn-tool ${this.currentTool === 'sticky' ? 'active' : ''}" data-tool="sticky" title="Sticky Note">
            📝 Sticky
          </button>
        `;
    }
  }

  private initCanvas(container: HTMLElement) {
    const canvasContainer = container.querySelector('#whiteboard-canvas-container') as HTMLElement;
    this.canvas = container.querySelector('#whiteboard-canvas') as HTMLCanvasElement;
    if (!this.canvas || !canvasContainer) return;

    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) return;

    const rect = canvasContainer.getBoundingClientRect();
    const width = Math.max(rect.width || 0, 800);
    const height = Math.max(rect.height || 0, 500);
    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = Math.floor(width * dpr);
    this.canvas.height = Math.floor(height * dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.redrawCanvas();
  }

  private redrawCanvas() {
    if (!this.canvas || !this.ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const curPage = this.pages[this.currentPageIndex];

    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Apply DPR & user zoom
    this.ctx.scale(dpr * this.zoomScale, dpr * this.zoomScale);

    // Render all vector elements of current page
    renderElementsToCanvas(this.ctx, curPage.elements, 1);
  }

  private saveCurrentState() {
    const curPage = this.pages[this.currentPageIndex];
    this.initPageHistory(curPage.id);
    const stack = this.undoStacks.get(curPage.id)!;
    stack.push(JSON.parse(JSON.stringify(curPage.elements)));
    if (stack.length > 30) stack.shift();
    // Clear redo
    this.redoStacks.set(curPage.id, []);
  }

  private undo() {
    const curPage = this.pages[this.currentPageIndex];
    const undoStack = this.undoStacks.get(curPage.id);
    if (undoStack && undoStack.length > 0) {
      const redoStack = this.redoStacks.get(curPage.id)!;
      redoStack.push(JSON.parse(JSON.stringify(curPage.elements)));
      curPage.elements = undoStack.pop()!;
      this.redrawCanvas();
    }
  }

  private redo() {
    const curPage = this.pages[this.currentPageIndex];
    const redoStack = this.redoStacks.get(curPage.id);
    if (redoStack && redoStack.length > 0) {
      const undoStack = this.undoStacks.get(curPage.id)!;
      undoStack.push(JSON.parse(JSON.stringify(curPage.elements)));
      curPage.elements = redoStack.pop()!;
      this.redrawCanvas();
    }
  }

  public handleRemoteDraw(action: DrawAction) {
    const targetIdx = action.pageIndex !== undefined ? action.pageIndex : this.currentPageIndex;
    if (targetIdx >= this.pages.length) {
      while (this.pages.length <= targetIdx) {
        this.addPageInternal(false);
      }
    }
    const targetPage = this.pages[targetIdx];

    if (action.type === 'clear') {
      targetPage.elements = [];
      if (targetIdx === this.currentPageIndex) this.redrawCanvas();
      return;
    }

    if (action.type === 'page_add') {
      this.addPageInternal(false);
      return;
    }

    if (action.type === 'page_delete' && action.pageIndex !== undefined && this.pages.length > 1) {
      this.pages.splice(action.pageIndex, 1);
      if (this.currentPageIndex >= this.pages.length) {
        this.currentPageIndex = this.pages.length - 1;
      }
      this.updatePaginationUI();
      this.redrawCanvas();
      return;
    }

    if (action.type === 'element_add' && action.element) {
      targetPage.elements.push(action.element);
      if (targetIdx === this.currentPageIndex) this.redrawCanvas();
      return;
    }

    // Backward-compatible move / shape actions
    if (action.type === 'move' && action.startX !== undefined && action.startY !== undefined && action.endX !== undefined && action.endY !== undefined) {
      const el: WhiteboardElement = {
        id: `el-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        tool: (action.tool as WhiteboardTool) || 'pen',
        color: action.color || '#ffffff',
        size: action.size || 4,
        points: [
          { x: action.startX, y: action.startY },
          { x: action.endX, y: action.endY }
        ]
      };
      targetPage.elements.push(el);
      if (targetIdx === this.currentPageIndex) this.redrawCanvas();
    } else if (action.type === 'shape' && action.startX !== undefined && action.startY !== undefined && action.endX !== undefined && action.endY !== undefined) {
      const el: WhiteboardElement = {
        id: `el-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        tool: (action.tool as WhiteboardTool) || 'rect',
        color: action.color || '#ffffff',
        size: action.size || 4,
        startX: action.startX,
        startY: action.startY,
        endX: action.endX,
        endY: action.endY
      };
      targetPage.elements.push(el);
      if (targetIdx === this.currentPageIndex) this.redrawCanvas();
    }
  }

  private addPageInternal(broadcast = true) {
    const nextNum = this.pages.length + 1;
    const newPage: WhiteboardPage = {
      id: `page-${Date.now()}-${nextNum}`,
      pageNumber: nextNum,
      title: `Page ${nextNum}`,
      elements: []
    };
    this.pages.push(newPage);
    this.initPageHistory(newPage.id);
    this.currentPageIndex = this.pages.length - 1;

    if (broadcast && this.onDrawAction) {
      this.onDrawAction({ type: 'page_add', pageIndex: this.currentPageIndex });
    }
    this.updatePaginationUI();
    this.redrawCanvas();
  }

  private deleteCurrentPage() {
    if (this.pages.length <= 1) return;
    const deletedIdx = this.currentPageIndex;
    this.pages.splice(deletedIdx, 1);
    // Re-index page numbers
    this.pages.forEach((p, idx) => (p.pageNumber = idx + 1));
    if (this.currentPageIndex >= this.pages.length) {
      this.currentPageIndex = this.pages.length - 1;
    }

    if (this.onDrawAction) {
      this.onDrawAction({ type: 'page_delete', pageIndex: deletedIdx });
    }
    this.updatePaginationUI();
    this.redrawCanvas();
  }

  private updatePaginationUI() {
    if (!this.containerEl) return;
    const cur = this.pages[this.currentPageIndex];
    const counter = this.containerEl.querySelector('#lbl-page-counter');
    if (counter) counter.textContent = `Page ${cur.pageNumber} of ${this.pages.length}`;

    const prevBtn = this.containerEl.querySelector('#btn-page-prev') as HTMLButtonElement;
    if (prevBtn) prevBtn.disabled = this.currentPageIndex === 0;

    const nextBtn = this.containerEl.querySelector('#btn-page-next') as HTMLButtonElement;
    if (nextBtn) nextBtn.disabled = this.currentPageIndex === this.pages.length - 1;

    const delBtn = this.containerEl.querySelector('#btn-page-delete') as HTMLButtonElement;
    if (delBtn) delBtn.disabled = this.pages.length <= 1;
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#btn-close-wb')?.addEventListener('click', () => this.hide());

    // Subject switcher
    container.querySelectorAll('.wb-subject-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        container.querySelectorAll('.wb-subject-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        this.subjectMode = (tab as HTMLElement).getAttribute('data-subject') as SubjectMode || 'general';

        // Auto-switch to default tool for that mode
        if (this.subjectMode === 'database') this.currentTool = 'db_table';
        else if (this.subjectMode === 'electronics') this.currentTool = 'el_resistor';
        else if (this.subjectMode === 'flowchart') this.currentTool = 'fc_process';
        else if (this.subjectMode === 'math') this.currentTool = 'math_grid';
        else this.currentTool = 'pen';

        const toolContainer = container.querySelector('#wb-dynamic-tools');
        if (toolContainer) {
          toolContainer.innerHTML = this.renderSubjectToolsHTML();
          this.bindToolButtons(container);
        }
      });
    });

    this.bindToolButtons(container);

    // Color picker
    container.querySelectorAll('.color-dot').forEach(btn => {
      btn.addEventListener('click', () => {
        this.currentColor = (btn as HTMLElement).getAttribute('data-color') || '#60a5fa';
        container.querySelectorAll('.color-dot').forEach(d => ((d as HTMLElement).style.borderColor = 'transparent'));
        (btn as HTMLElement).style.borderColor = 'white';
      });
    });

    // Stroke size
    const sizeInput = container.querySelector('#input-stroke-size') as HTMLInputElement;
    sizeInput?.addEventListener('input', () => {
      this.currentSize = parseInt(sizeInput.value, 10) || 4;
    });

    // Undo / Redo
    container.querySelector('#btn-wb-undo')?.addEventListener('click', () => this.undo());
    container.querySelector('#btn-wb-redo')?.addEventListener('click', () => this.redo());

    // Clear Canvas
    container.querySelector('#btn-wb-clear')?.addEventListener('click', () => {
      if (confirm(`Clear all drawings on Page ${this.currentPageIndex + 1}?`)) {
        this.saveCurrentState();
        this.pages[this.currentPageIndex].elements = [];
        this.redrawCanvas();
        if (this.onDrawAction) {
          this.onDrawAction({ type: 'clear', pageIndex: this.currentPageIndex });
        }
      }
    });

    // Zoom Controls
    container.querySelector('#btn-zoom-in')?.addEventListener('click', () => {
      this.zoomScale = Math.min(3.0, this.zoomScale + 0.25);
      this.updateZoomUI(container);
      this.redrawCanvas();
    });
    container.querySelector('#btn-zoom-out')?.addEventListener('click', () => {
      this.zoomScale = Math.max(0.5, this.zoomScale - 0.25);
      this.updateZoomUI(container);
      this.redrawCanvas();
    });
    container.querySelector('#btn-zoom-reset')?.addEventListener('click', () => {
      this.zoomScale = 1.0;
      this.updateZoomUI(container);
      this.redrawCanvas();
    });

    // Multi-page controls
    container.querySelector('#btn-page-prev')?.addEventListener('click', () => {
      if (this.currentPageIndex > 0) {
        this.currentPageIndex--;
        this.updatePaginationUI();
        this.redrawCanvas();
      }
    });
    container.querySelector('#btn-page-next')?.addEventListener('click', () => {
      if (this.currentPageIndex < this.pages.length - 1) {
        this.currentPageIndex++;
        this.updatePaginationUI();
        this.redrawCanvas();
      }
    });
    container.querySelector('#btn-page-add')?.addEventListener('click', () => {
      this.addPageInternal(true);
    });
    container.querySelector('#btn-page-delete')?.addEventListener('click', () => {
      if (confirm(`Delete Page ${this.currentPageIndex + 1}? Drawings will be removed.`)) {
        this.deleteCurrentPage();
      }
    });

    // Download Artifact Modal Trigger
    container.querySelector('#btn-download-artifact')?.addEventListener('click', () => {
      this.showExportDialog(container);
    });

    // Canvas Pointer Events
    this.bindCanvasDrawing(container);
  }

  private updateZoomUI(container: HTMLElement) {
    const lbl = container.querySelector('#lbl-zoom-level');
    if (lbl) lbl.textContent = `${Math.round(this.zoomScale * 100)}%`;
  }

  private bindToolButtons(container: HTMLElement) {
    container.querySelectorAll('.btn-tool').forEach(btn => {
      btn.addEventListener('click', () => {
        container.querySelectorAll('.btn-tool').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.currentTool = (btn as HTMLElement).getAttribute('data-tool') as WhiteboardTool || 'pen';
      });
    });
  }

  private bindCanvasDrawing(_container?: HTMLElement) {
    const canvasEl = this.canvas;
    if (!canvasEl) return;

    const getPos = (clientX: number, clientY: number) => {
      const rect = canvasEl.getBoundingClientRect();
      const rawX = clientX - rect.left;
      const rawY = clientY - rect.top;
      return {
        x: rawX / this.zoomScale,
        y: rawY / this.zoomScale,
      };
    };

    const isFreehand = (tool: WhiteboardTool) => tool === 'pen' || tool === 'highlighter' || tool === 'eraser';

    const onPointerDown = (clientX: number, clientY: number) => {
      if (!this.ctx || !canvasEl) return;
      this.isDrawing = true;
      const pos = getPos(clientX, clientY);
      this.startX = pos.x;
      this.startY = pos.y;
      this.currentStrokePoints = [pos];

      this.saveCurrentState();

      if (isFreehand(this.currentTool)) {
        // Draw immediate dot
        const dpr = window.devicePixelRatio || 1;
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(dpr * this.zoomScale, dpr * this.zoomScale);
        this.ctx.beginPath();
        this.ctx.arc(pos.x, pos.y, Math.max(1, this.currentSize / 2), 0, Math.PI * 2);
        this.ctx.fillStyle = this.currentTool === 'eraser' ? 'rgba(0,0,0,1)' : this.currentColor;
        this.ctx.fill();
      }
    };

    const onPointerMove = (clientX: number, clientY: number) => {
      if (!this.isDrawing || !this.ctx || !canvasEl) return;
      const pos = getPos(clientX, clientY);

      if (isFreehand(this.currentTool)) {
        this.currentStrokePoints.push(pos);
        // Live drawing preview
        const dpr = window.devicePixelRatio || 1;
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(dpr * this.zoomScale, dpr * this.zoomScale);
        this.ctx.lineWidth = this.currentSize;
        this.ctx.strokeStyle = this.currentTool === 'eraser' ? 'rgba(0,0,0,1)' : this.currentColor;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        this.ctx.beginPath();
        this.ctx.moveTo(this.startX, this.startY);
        this.ctx.lineTo(pos.x, pos.y);
        this.ctx.stroke();

        if (this.onDrawAction) {
          this.onDrawAction({
            type: 'move',
            tool: this.currentTool,
            color: this.currentColor,
            size: this.currentSize,
            startX: this.startX,
            startY: this.startY,
            endX: pos.x,
            endY: pos.y,
            pageIndex: this.currentPageIndex,
          });
        }

        this.startX = pos.x;
        this.startY = pos.y;
      } else {
        // Preview shape over canvas
        this.redrawCanvas();
        const dpr = window.devicePixelRatio || 1;
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(dpr * this.zoomScale, dpr * this.zoomScale);

        const tempElement: WhiteboardElement = {
          id: 'temp',
          tool: this.currentTool,
          color: this.currentColor,
          size: this.currentSize,
          startX: this.startX,
          startY: this.startY,
          endX: pos.x,
          endY: pos.y,
        };
        renderElementsToCanvas(this.ctx, [tempElement], 1);
      }
    };

    const onPointerUp = (clientX: number, clientY: number) => {
      if (!this.isDrawing || !this.ctx || !canvasEl) return;
      this.isDrawing = false;
      const pos = getPos(clientX, clientY);
      const curPage = this.pages[this.currentPageIndex];

      let newElement: WhiteboardElement | null = null;

      if (isFreehand(this.currentTool)) {
        if (this.currentStrokePoints.length > 0) {
          newElement = {
            id: `el-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            tool: this.currentTool,
            color: this.currentColor,
            size: this.currentSize,
            points: [...this.currentStrokePoints],
          };
        }
      } else {
        // Complex shape / component
        newElement = {
          id: `el-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          tool: this.currentTool,
          color: this.currentColor,
          size: this.currentSize,
          startX: this.startX,
          startY: this.startY,
          endX: pos.x,
          endY: pos.y,
        };
      }

      if (newElement) {
        curPage.elements.push(newElement);
        this.redrawCanvas();

        if (this.onDrawAction) {
          this.onDrawAction({
            type: 'element_add',
            pageIndex: this.currentPageIndex,
            element: newElement,
          });
        }
      }
      this.currentStrokePoints = [];
    };

    canvasEl.addEventListener('mousedown', (e) => onPointerDown(e.clientX, e.clientY));
    window.addEventListener('mousemove', (e) => onPointerMove(e.clientX, e.clientY));
    window.addEventListener('mouseup', (e) => onPointerUp(e.clientX, e.clientY));

    canvasEl.addEventListener('touchstart', (e) => {
      if (e.touches.length > 0) onPointerDown(e.touches[0].clientX, e.touches[0].clientY);
    });
    window.addEventListener('touchmove', (e) => {
      if (e.touches.length > 0) onPointerMove(e.touches[0].clientX, e.touches[0].clientY);
    });
    window.addEventListener('touchend', (e) => {
      if (e.changedTouches.length > 0) onPointerUp(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    });
  }

  /**
   * High-Resolution & Vector Export Modal Dialog
   */
  private showExportDialog(container: HTMLElement) {
    const mount = container.querySelector('#wb-export-modal-mount');
    if (!mount) return;

    mount.innerHTML = `
      <div class="modal-backdrop" style="z-index: 1050; padding: 1rem;">
        <div class="wb-export-dialog">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div style="font-weight: 800; font-size: 1.15rem; color: white; display: flex; align-items: center; gap: 8px;">
              <span>💾</span> Export Whiteboard Artifact
            </div>
            <button type="button" id="btn-close-export-modal" class="control-btn" style="width: 28px; height: 28px;">✕</button>
          </div>

          <p style="font-size: 0.85rem; color: var(--text-secondary); margin: 0;">
            Export crystal-clear vector or super-sampled raster artifacts that never blur on zoom.
          </p>

          <!-- Format Choice -->
          <div>
            <label style="font-size: 0.8rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px; display: block;">
              Export Format
            </label>
            <div class="wb-export-grid">
              <div class="wb-export-option-card selected" data-format="svg">
                <div style="font-weight: 700; font-size: 0.9rem; color: #38bdf8;">⚡ Vector SVG</div>
                <div style="font-size: 0.75rem; color: var(--text-secondary); margin-top: 2px;">
                  Infinite resolution. Always razor sharp on any zoom level.
                </div>
              </div>

              <div class="wb-export-option-card" data-format="pdf">
                <div style="font-weight: 700; font-size: 0.9rem; color: #f43f5e;">📄 Multi-Page PDF</div>
                <div style="font-size: 0.75rem; color: var(--text-secondary); margin-top: 2px;">
                  Unified multi-page document for all whiteboard pages.
                </div>
              </div>

              <div class="wb-export-option-card" data-format="png">
                <div style="font-weight: 700; font-size: 0.9rem; color: #10b981;">🖼️ Ultra-HD PNG</div>
                <div style="font-size: 0.75rem; color: var(--text-secondary); margin-top: 2px;">
                  Super-sampled 4K/8K/16K crystal-clear raster image.
                </div>
              </div>

              <div class="wb-export-option-card" data-format="jpg">
                <div style="font-weight: 700; font-size: 0.9rem; color: #fbbf24;">🌅 High-Res JPG</div>
                <div style="font-size: 0.75rem; color: var(--text-secondary); margin-top: 2px;">
                  Compressed high-definition photograph format.
                </div>
              </div>
            </div>
          </div>

          <!-- Scope Choice: Current Page vs All Pages -->
          <div>
            <label style="font-size: 0.8rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px; display: block;">
              Page Selection
            </label>
            <div style="display: flex; gap: 8px;">
              <label style="flex: 1; display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.05); padding: 8px 12px; border-radius: var(--radius-md); cursor: pointer; font-size: 0.85rem;">
                <input type="radio" name="export-scope" value="current" checked />
                <span>Current Page (${this.currentPageIndex + 1})</span>
              </label>
              <label style="flex: 1; display: flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.05); padding: 8px 12px; border-radius: var(--radius-md); cursor: pointer; font-size: 0.85rem;">
                <input type="radio" name="export-scope" value="all" />
                <span>All Pages (${this.pages.length} Pages)</span>
              </label>
            </div>
          </div>

          <!-- Resolution Scale for Raster (PNG/JPG) -->
          <div id="export-resolution-section" style="display: none;">
            <label style="font-size: 0.8rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; margin-bottom: 6px; display: block;">
              Raster Super-Sampling Resolution
            </label>
            <select id="select-export-scale" style="width: 100%; padding: 8px 12px; background: rgba(255,255,255,0.08); border: 1px solid var(--border-subtle); color: white; border-radius: var(--radius-md); font-size: 0.85rem;">
              <option value="1">1080p Standard (1x)</option>
              <option value="4" selected>4K Ultra-HD (4x Scale - ~3840×2160)</option>
              <option value="8">8K Extreme Ultra-HD (8x Scale - ~7680×4320)</option>
              <option value="16">16K Super-Sampled Master (16x Scale)</option>
            </select>
          </div>

          <!-- Download Action -->
          <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 0.5rem;">
            <button type="button" id="btn-cancel-export" class="btn-secondary">Cancel</button>
            <button type="button" id="btn-confirm-export" class="btn-primary" style="padding: 8px 20px;">
              Download Artifact
            </button>
          </div>
        </div>
      </div>
    `;

    let selectedFormat = 'svg';

    mount.querySelectorAll('.wb-export-option-card').forEach(card => {
      card.addEventListener('click', () => {
        mount.querySelectorAll('.wb-export-option-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        selectedFormat = (card as HTMLElement).getAttribute('data-format') || 'svg';

        const resSection = mount.querySelector('#export-resolution-section') as HTMLElement;
        if (resSection) {
          resSection.style.display = selectedFormat === 'png' || selectedFormat === 'jpg' ? 'block' : 'none';
        }
      });
    });

    mount.querySelector('#btn-close-export-modal')?.addEventListener('click', () => (mount.innerHTML = ''));
    mount.querySelector('#btn-cancel-export')?.addEventListener('click', () => (mount.innerHTML = ''));

    mount.querySelector('#btn-confirm-export')?.addEventListener('click', async () => {
      const scope = (mount.querySelector('input[name="export-scope"]:checked') as HTMLInputElement)?.value || 'current';
      const scaleInput = (mount.querySelector('#select-export-scale') as HTMLSelectElement)?.value || '4';
      const scale = parseInt(scaleInput, 10) || 4;

      const confirmBtn = mount.querySelector('#btn-confirm-export') as HTMLButtonElement;
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerText = 'Generating...';
      }

      try {
        await this.executeExport(selectedFormat, scope, scale);
      } finally {
        mount.innerHTML = '';
      }
    });
  }

  private async executeExport(format: string, scope: string, scale: number) {
    if (!this.canvas) return;
    const baseW = Math.round(this.canvas.width / (window.devicePixelRatio || 1));
    const baseH = Math.round(this.canvas.height / (window.devicePixelRatio || 1));
    const pagesToExport = scope === 'all' ? this.pages : [this.pages[this.currentPageIndex]];

    if (format === 'svg') {
      for (const page of pagesToExport) {
        const svgContent = generatePageSVG(page, baseW, baseH, this.roomId);
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        downloadBlob(blob, `verve-whiteboard-${this.roomId}-page${page.pageNumber}.svg`);
      }
    } else if (format === 'pdf') {
      const pdfBlob = await generateMultiPagePDF(pagesToExport, baseW, baseH, this.roomId);
      downloadBlob(pdfBlob, `verve-whiteboard-${this.roomId}-all-pages.pdf`);
    } else {
      // PNG or JPG
      const imgFormat = format === 'jpg' ? 'jpeg' : 'png';
      for (const page of pagesToExport) {
        const dataUrl = await renderPageHighRes(page, baseW, baseH, scale, imgFormat, this.roomId);
        const link = document.createElement('a');
        link.download = `verve-whiteboard-${this.roomId}-page${page.pageNumber}-${scale}x.${format}`;
        link.href = dataUrl;
        link.click();
      }
    }
  }

  /**
   * Default high-res multi-page export invoked automatically on meeting exit
   */
  public async exportAllPagesDefault() {
    if (!this.canvas) return;
    const baseW = Math.round(this.canvas.width / (window.devicePixelRatio || 1));
    const baseH = Math.round(this.canvas.height / (window.devicePixelRatio || 1));
    const pdfBlob = await generateMultiPagePDF(this.pages, baseW, baseH, this.roomId);
    downloadBlob(pdfBlob, `verve-whiteboard-${this.roomId}-final-saved.pdf`);
  }
}
